import React from 'react';
import { bool, object } from 'prop-types';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { LINE_ITEM_SHIPPING_FEE, propTypes } from '../../util/types';

import css from './OrderBreakdown.module.css';

/**
 * The postage row.
 *
 * A shipped order has no postage on it until the buyer has given a delivery
 * address, because the price is the carrier's real price for that address
 * (P25028-121). Until then the row still appears, saying so — a buyer shown
 * nothing, or shown a zero, would reasonably read it as free postage and be
 * surprised at the total (P25028-122).
 */
const LineItemShippingFeeMaybe = props => {
  const { lineItems, isShipping, shippingQuote, offPlatformShipping, intl } = props;

  const shippingFeeLineItem = lineItems.find(
    item => item.code === LINE_ITEM_SHIPPING_FEE && !item.reversal
  );

  if (shippingFeeLineItem) {
    return (
      <>
        <div className={css.lineItem}>
          <span className={css.itemLabel}>
            <FormattedMessage id="OrderBreakdown.shippingFee" />
          </span>
          <span className={css.itemValue}>{formatMoney(intl, shippingFeeLineItem.lineTotal)}</span>
        </div>

        {/* Which service the postage buys, said before the buyer pays. Every
            service is a drop-off (P25028-271): the seller carries the parcel to a
            shop. The two keys differ only in whether the shop is named. */}
        {shippingQuote?.carrier ? (
          <p className={css.shippingService}>
            <FormattedMessage
              id={
                shippingQuote.isDropOff
                  ? 'OrderBreakdown.shippingServiceDropOff'
                  : 'OrderBreakdown.shippingServiceCollected'
              }
              values={{ carrier: shippingQuote.carrier, service: shippingQuote.service || '' }}
            />
          </p>
        ) : null}
      </>
    );
  }

  // No amount for the seller: they are paid none of the postage since P25028-121.
  // But they do need to know which carrier's shop the parcel goes to, so the
  // service is named for them too (P25028-123, P25028-271).
  if (shippingQuote?.carrier) {
    return (
      <p className={css.shippingService}>
        <FormattedMessage
          id={
            shippingQuote.isDropOff
              ? 'OrderBreakdown.shippingServiceDropOff'
              : 'OrderBreakdown.shippingServiceCollected'
          }
          values={{ carrier: shippingQuote.carrier, service: shippingQuote.service || '' }}
        />
      </p>
    );
  }

  // P25028-132: no postage, and none is coming. A parcel no carrier will take is
  // arranged between the buyer and the seller, so the "worked out at checkout"
  // wording below would be a promise this order never keeps — and it would sit
  // directly under a notice saying the opposite.
  if (offPlatformShipping) {
    return (
      <div className={css.lineItem}>
        <span className={css.itemLabel}>
          <FormattedMessage id="OrderBreakdown.shippingFee" />
        </span>
        <span className={css.itemValue}>
          <FormattedMessage id="OrderBreakdown.shippingArrangedOffPlatform" />
        </span>
      </div>
    );
  }

  return isShipping ? (
    <div className={css.lineItem}>
      <span className={css.itemLabel}>
        <FormattedMessage id="OrderBreakdown.shippingFee" />
      </span>
      <span className={css.itemValue}>
        <FormattedMessage id="OrderBreakdown.shippingFeePending" />
      </span>
    </div>
  ) : null;
};

LineItemShippingFeeMaybe.defaultProps = {
  isShipping: false,
  shippingQuote: null,
  offPlatformShipping: false,
};

LineItemShippingFeeMaybe.propTypes = {
  lineItems: propTypes.lineItems.isRequired,
  // True only when this is the buyer's view of an order being posted.
  isShipping: bool,
  // The quote behind the figure: which carrier and service, and whether the seller
  // has to drop the parcel off. From protectedData.shippingQuote.
  shippingQuote: object,
  // True when this parcel is too big for any carrier, so postage is arranged
  // directly between the two sides. From protectedData.shippingArrangedOffPlatform.
  offPlatformShipping: bool,
  intl: intlShape.isRequired,
};

export default LineItemShippingFeeMaybe;
